import { useCallback, useEffect, useState } from 'react';
import feedService, { mapPostReactionUsersToLikedUsers } from '@wlb/shared/api/services/feedService';
import {
  getUserFacingErrorMessage,
  UnusableResponseError,
} from '@wlb/shared/utils/apiErrorHandler';
import { normalizeUserGuid } from '@wlb/shared/utils/navigateToUserProfile';
import { useProfile } from '@wlb/shared/context/ProfileContext';
import { useAuth } from '@wlb/shared/context/AuthContext';
import type { LikedUser } from '@wlb/shared/types/post';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { HeartIcon } from '../../components/ui/icons';
import { ListStatus } from '../../components/ui/ListStatus';
import { Modal } from '../../components/ui/Modal';
import { avatarFallback } from '../../components/ui/avatarFallback';
import '../../components/ui/avatar.css';
import { useOpenUserProfile } from '../profile/useOpenUserProfile';
import { getUsernameDisplay } from '../../utils/usernameDisplay';
import { FeedUserRowsSkeleton } from './FeedModalSkeleton';
import './feedModal.css';

interface LikesModalProps { postId: string; onClose: () => void; }

export function LikesModal({ postId, onClose }: LikesModalProps) {
  const { t } = useTranslation();
  const { userId: authUserId, userGUID } = useAuth();
  const openUserProfile = useOpenUserProfile();
  const { profile, userRelation } = useProfile();
  const [users, setUsers] = useState<LikedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [following, setFollowing] = useState<Record<string, boolean>>({});
  const [followLoading, setFollowLoading] = useState<Record<string, boolean>>({});

  const currentUserId = String(authUserId || profile?.userId || '');
  const currentUserGuid = userGUID ?? profile?.userGuid ?? null;

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await feedService.getPostReactionList(postId);
      // A rejected request is not a post nobody liked.
      if (!result.success) {
        throw new UnusableResponseError('GetPostReactionList');
      }
      setUsers(mapPostReactionUsersToLikedUsers(result.data));
    } catch (err) {
      setError(getUserFacingErrorMessage(err, t('common.errors.generic')));
    } finally {
      setLoading(false);
    }
  }, [postId, t]);

  useEffect(() => { loadUsers().catch(() => undefined); }, [loadUsers]);

  const follow = async (userId: string) => {
    if (followLoading[userId] || following[userId]) return;
    setFollowLoading(current => ({ ...current, [userId]: true }));
    try {
      await userRelation(Number(userId), 'Follow');
      setFollowing(current => ({ ...current, [userId]: true }));
    } catch {
      // ProfileContext exposes the translated API error through its shared state.
    } finally {
      setFollowLoading(current => ({ ...current, [userId]: false }));
    }
  };

  return (
    <Modal title={t('likes.title')} onClose={onClose} labelledBy="likes-modal-title">
      <div className="likes-modal__list" aria-live="polite">
        {loading ? (
          <FeedUserRowsSkeleton />
        ) : error ? (
          <ListStatus variant="error" layout="inline" message={error} onAction={() => { loadUsers().catch(() => undefined); }} />
        ) : users.length === 0 ? (
          <ListStatus variant="empty" layout="inline" title={t('likes.empty')} />
        ) : users.map(user => {
          const userGuid = normalizeUserGuid(user.profileUserGuid) ?? normalizeUserGuid(user.userGuid);
          const displayName = getUsernameDisplay(user);
          const isCurrentUser = 
            (currentUserId !== '' && String(user.id) === currentUserId) ||
            (currentUserGuid != null && 
              (normalizeUserGuid(user.profileUserGuid) === normalizeUserGuid(currentUserGuid) || 
               normalizeUserGuid(user.userGuid) === normalizeUserGuid(currentUserGuid))
            );
          const fallback = avatarFallback({ seed: userGuid ?? user.id, name: displayName, username: user.name });
          const identity = (
            <>
              {user.avatar ? (
                <img src={user.avatar} alt="" />
              ) : (
                <span className="likes-modal__avatar" data-avatar-tone={fallback.tone} aria-hidden>
                  {fallback.initial}
                </span>
              )}
              <span>
                <strong>
                  {displayName}
                  {isCurrentUser ? ` ${t('likes.you')}` : ''}
                </strong>
                {user.isBuddy && <small>{t('likes.buddy')}</small>}
                {user.currentWeight && <small>{user.currentWeight}</small>}
              </span>
            </>
          );
          return (
            <div className="likes-modal__user" key={user.id}>
              {userGuid ? (
                <button className="likes-modal__identity" type="button" onClick={() => openUserProfile(userGuid)} aria-label={t('likes.viewProfile', { name: displayName })}>{identity}</button>
              ) : (
                <div className="likes-modal__identity likes-modal__identity--static">{identity}</div>
              )}
              <button className="likes-modal__follow" type="button" onClick={() => { follow(user.id).catch(() => undefined); }} disabled={followLoading[user.id] || following[user.id]}>{following[user.id] ? t('likes.following') : t('likes.follow')}</button>
              <HeartIcon size={18} fill="currentColor" />
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
